<i18n>
{
  "fr": {
    "tool-title": "Etat au",
    "no-data-title": "Aucune donnée",
    "goto-data": "Accéder aux données",
    "week-1": "-7 jours",
    "week-2": "-14 jours",
    "week-3": "-21 jours",
    "past-evolution": "Tendances",
    "7-days": "7 jours",
    "14-days": "14 jours",
    "21-days": "21 jours",
    "no-remote-data": "Aucune donnée télétransmise disponible.",
    "no-data": "Aucune donnée disponible",
    "downModerate": "Baisse forte (> 25 cm)",
    "downHigh": "Baisse modérée (10 à 25 cm)",
    "stable": "Stable (± 10cm)",
    "upModerate": "Hausse modérée (10 à 25cm)",
    "upHigh": "Hausse forte (> 25 cm)",
    "last-forecast-generation": "Dernière prévisions",
    "last-forecast-generation-tooltip": "Date à laquelle les dernières prévisions ont été générées",
    "graph-legend-title": "Légendes:",
    "observation-label": "Observations",
    "forecast-label": "Prévisions"
  },
  "de": {

  }
}
</i18n>

<template>
  <div id="last-remote-measurement" class="tool" v-if="show">
    <div class="title">
      <h3 class="tool-title">{{$t('tool-title')}} {{ getMaxDate() | formatDate(userUtcOffset) }} {{ getMaxDate() | formatTime(userUtcOffset) }} {{ utcOffsetDisplay }} </h3>
    </div>
    <div class="tool-content">
      <div v-if="showGraph">
        <GroundwaterLevel
          :observationPoint="currentObject"
          :dates="dateRange"
          aggregation="dd"
          hoverformat="%d.%m.%Y"
          tickformat="%d.%m.%Y"
          :showTickRangeOnly="true"
          :tickBarRange="{ size: windowSize/7, unit: 'days' }"
          :showLegends="false"
          :fixedRange="true"
          :showAxisRangeSelect="false"
          :showForecast="hasForecastData"
          :centerBarEvent="referenceEvent"
          :forecastReferenceEvent="lastForecastReferenceEvent"
          :showParameterTypeSelect="false"
          :showThreshold="true"
          defaultAxisRangeMode="auto"
        />
        <div class="graph-info">
          <p v-if="hasForecastData">
            <span>{{ $t('last-forecast-generation') }}: {{ lastReferenceValue.event | formatDate(userUtcOffset) }}</span>
            <md-tooltip>{{ $t('last-forecast-generation-tooltip') }}</md-tooltip>
          </p>
          <div class="graph-legends">
            <p>{{ $t('graph-legend-title') }}</p>
            <div class="graph-legend">
              <span class="legend-linecolor obs"></span>
              <span class="legend-title">{{ $t('observation-label') }}</span>
            </div>
            <div class="graph-legend" v-if="hasForecastData">
              <span class="legend-linecolor forecast"></span>
              <span class="legend-title">{{ $t('forecast-label') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="no-data" v-if="nodata && !loading">
        <md-icon>info</md-icon>
        <span class="info-text" v-if="hasRemoteData">{{ $t('no-remote-data') }}</span>
        <span class="info-text nodata" v-if="!hasRemoteData">{{ $t('no-data') }}</span>
      </div>

      <md-button v-if="!(!hasRemoteData && nodata)" class="show-timeseries md-raised" @click="gotoTimeseries()">{{ $t('goto-data') }}</md-button>

      <div v-if="showPastEvolution" class="past-evolution">
        <h3>{{ $t('past-evolution') }}</h3>
        <md-table>
          <md-table-row>
            <md-table-head>{{ $t('week-1') }}</md-table-head>
            <md-table-cell>
              <img class="past-week-icon" :src="getEvolutionImg(pastEvolution.pastWeekDelta)" />
              <md-tooltip>{{ getEvolutionTooltip(pastEvolution.pastWeekDelta) }}</md-tooltip>
            </md-table-cell>
            <md-table-cell>{{ pastEvolution.pastWeekDelta | formatPastEvolutionValue }} {{ pastEvolutionDatasource.parameterType.unit.symbol[$i18n.locale] }}</md-table-cell>
          </md-table-row>
          <md-table-row>
            <md-table-head>{{ $t('week-2') }}</md-table-head>
            <md-table-cell>
              <img class="past-week-icon" :src="getEvolutionImg(pastEvolution.past2weeksDelta)" />
              <md-tooltip>{{ getEvolutionTooltip(pastEvolution.past2weeksDelta) }}</md-tooltip>
            </md-table-cell>
            <md-table-cell>{{ pastEvolution.past2weeksDelta | formatPastEvolutionValue }} {{ pastEvolutionDatasource.parameterType.unit.symbol[$i18n.locale] }}</md-table-cell>
          </md-table-row>
          <md-table-row>
            <md-table-head>{{ $t('week-3') }}</md-table-head>
            <md-table-cell>
              <img class="past-week-icon" :src="getEvolutionImg(pastEvolution.past3weeksDelta)" />
              <md-tooltip>{{ getEvolutionTooltip(pastEvolution.past3weeksDelta) }}</md-tooltip>
            </md-table-cell>
            <md-table-cell>{{ pastEvolution.past3weeksDelta | formatPastEvolutionValue}} {{ pastEvolutionDatasource.parameterType.unit.symbol[$i18n.locale] }}</md-table-cell>
          </md-table-row>
        </md-table>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'

import MapStoreMixin from '@/mixins/store/map'
import LayerStoreMixin from '@/mixins/store/layer'
import TimeStoreMixin from '@/mixins/store/time'
import GroundwaterLevel from '@/components/timeseries/graphs/groundwaterLevel'

import downModerate from '@/assets/icons/past-evolution/down-moderate.png'
import downHigh from '@/assets/icons/past-evolution/down-high.png'
import stable from '@/assets/icons/past-evolution/stable.png'
import upModerate from '@/assets/icons/past-evolution/up-moderate.png'
import upHigh from '@/assets/icons/past-evolution/up-high.png'

import TimeseriesProxy from '@/proxies/timeseries'

export default {
  name: 'WarningStateLevel',
  mixins: [
    MapStoreMixin,
    LayerStoreMixin,
    TimeStoreMixin
  ],
  components: {
    GroundwaterLevel
  },
  props: {
    params: {
      type: Object,
      default: () => { return {} }
    }
  },
  filters: {
    formatValue (value) {
      return value === null ? '-' : value.toFixed(2)
    },
    formatPastEvolutionValue (value) {
      return `${value >= 0 ? '+' : '-'} ${Math.abs(value.toFixed(2))}`
    }
  },
  computed: {
    isObservationPointLoading () {
      return _.isEmpty(this.currentObject) && this.currentGeom !== null
    },
    show () {
      return !_.isEmpty(this.currentObject)
    },
    showGraph () {
      return !this.loading && this.referenceEvent !== null
    },
    availableParameterTypes () {
      let toReturn = new Map()
      this.datasources.forEach((datasource) => {
        toReturn.set(datasource.parameterType.id, datasource.parameterType)
      })
      return _.orderBy(Array.from(toReturn, ([key, value]) => value), ['code'], ['asc']) // eslint-disable-line no-unused-vars
    },
    datasources () {
      if (_.isEmpty(this.currentObject)) return []
      return this.currentObject.datasources.filter((ds) => _.includes(this.params.parameterTypeCodes, ds.parameterType.code))
    },
    pastEvolutionDatasource () {
      return this.datasources.find((datasource) => {
        return datasource.parameterType.code === 'HEAD_BSL' && datasource.datasourceType.code === 'remote'
      })
    },
    dateRange () {
      let toDate = moment.utc(this.referenceEvent).add(this.windowSize, 'days')
      let fromDate = moment.utc(this.referenceEvent).subtract(this.windowSize, 'days')
      return [fromDate.toISOString(), toDate.toISOString()]
    },
    referenceEvent () {
      // On centre le graphique à aujourd'hui 00:00 UTC
      return moment.utc().set({hour:0,minute:0,second:0,millisecond:0})
    },
    lastForecastReferenceEvent () {
      if (this.lastReferenceValue === null) return null
      return moment.utc(this.lastReferenceValue.event).set({hour:0,minute:0,second:0,millisecond:0}).add(1, 'days')
    },
    workingState () {
      let datasources = this.datasources.filter((ds) => _.includes(this.params.parameterTypeCodes, ds.parameterType.code))
      let datasourcesNodata = this.hasRemoteData ? this.datasources.filter((ds) => ds.workingState === 'nodata' && ds.datasourceType.code === 'remote') : this.datasources.filter((ds) => ds.workingState === 'nodata' && ds.datasourceType === 'validated')
      let datasourcesWarn = datasources.filter((ds) => ds.workingState === 'warn')
      let datasourcesErr = datasources.filter((ds) => ds.workingState === 'err')

      if (datasourcesNodata.length > 0) return 'nodata'
      if (datasourcesErr.length > 0) return 'err'
      if (datasourcesWarn.length > 0) return 'warn'
      return 'ok'
    },
    showPastEvolution () {
      return this.pastEvolution !== null && this.workingState === 'ok'
    },
    hasRemoteData () {
      return this.datasources.filter((ds) => ds.datasourceType.code === 'remote').length > 0
    },
    hasForecastData () {
      return  this.currentObject.datasources.filter((ds) => ds.parameterType.code === 'HEADS_FORECAST_7').length > 0
    },
    noRemoteData () {
      return this.hasRemoteData && this.datasources.filter((ds) => ds.datasourceType.code === 'remote' && ds.stats.maxDate === null).length > 0
    },
    nodata () {
      return this.workingState === 'nodata' || this.noRemoteData
    }
  },
  watch: {
    isObservationPointLoading() {
      if (this.isObservationPointLoading === false) this.load().catch(this.handleError)
      else this.loading = true
    }
  },
  data () {
    return {
      pastEvolution: null,
      windowSize: 21,
      lastReferenceValue: null,
      loading: true
    }
  },
  methods: {
    load () {
      return new Promise((resolve, reject) => {
        this.loading = true
        let toFetch = [
          this.fetchLastForecastValue(),
          this.fetchPastEvolution()
        ]

        Promise.all(toFetch).then(() => {
          this.loading = false
          resolve()
        }).catch(reject)
      })
    },
    fetchPastEvolution () {
      this.pastEvolution = null
      if (typeof this.pastEvolutionDatasource === 'undefined') return
      TimeseriesProxy.getPastEvolution(this.pastEvolutionDatasource.id).then((pastEvolution) => { this.pastEvolution = pastEvolution })
    },
    fetchLastForecastValue () {
        this.lastReferenceValue = null
        let forecastDatasource = this.currentObject.datasources.find((datasource) => datasource.parameterType.code === 'HEADS_FORECAST_7')
        if (typeof forecastDatasource === 'undefined') return
        TimeseriesProxy.getLastValue(forecastDatasource.id, false).then((lastReferenceValue) => this.lastReferenceValue = lastReferenceValue)
    },
    isGraphVisible (parameterTypeCodes) {
      return parameterTypeCodes.filter(x => this.params.parameterTypeCodes.includes(x)).length > 0
    },
    gotoTimeseries () {
      let routeData = this.$router.resolve({name: 'observation-point-data', params: { id: this.currentObject.id }});
      window.open(routeData.href, '_blank');
    },
    getEvolutionImg (delta) {
      if (delta >= 0.25) return upHigh
      if (delta > 0.1 && delta < 0.25) return upModerate
      if (delta >= -0.1 && delta <= 0.1) return stable
      if (delta < -0.1 && delta > -0.25) return downModerate
      if (delta <= -0.25) return downHigh

      return ''
    },
    getEvolutionTooltip (delta) {
      if (delta >= 0.25) return this.$i18n.t('upHigh')
      if (delta > 0.1 && delta < 0.25) return this.$i18n.t('upModerate')
      if (delta >= -0.1 && delta <= 0.1) return this.$i18n.t('stable')
      if (delta < -0.1 && delta > -0.25) return this.$i18n.t('downModerate')
      if (delta <= -0.25) return this.$i18n.t('downHigh')

      return ''
    },
    getMaxDate () {
      let toReturn = moment.utc(this.datasources[0].stats.maxDate)
      this.datasources.forEach((datasource) => {
        if (toReturn.isBefore(datasource.stats.maxDate) || !toReturn.isValid()) toReturn = moment.utc(datasource.stats.maxDate)
      })
      return toReturn
    },
    handleError (e) {
      console.log(e)
    }
  }
}
</script>

<style lang="scss" scoped>
  #last-remote-measurement {
    width: 100%;

    .tool-content {
      display: flex;
      flex-direction: column;

      .graph-info {
        user-select: none;
        padding: 0px 3px 0px 3px;

        .graph-legends {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-around;

          .graph-legend {
            display: flex;
            align-items: center;
            .legend-linecolor {
              display: block;
              width: 15px;
              height: 2px;
              margin-right: 10px;
              margin-left: 5px;

              &.obs { background-color: #a05b18; }
              &.forecast {background-color: #7d7e91;}
            }
          }
        }
      }
    }

    .empty-data {
      margin-top: 15px;
      margin-bottom: 15px;

      .md-icon {
        width: 80px;
        min-width: 80px;
        font-size: 80px !important;
      }
      .md-empty-state-label {
        font-size: 18px;
        line-height: 18px;
      }

      &.md-empty-state-leave-active {
        display: none !important;
      }
    }

    .last-values {
      border-bottom: 1px solid #e0e0e0;
    }

    .title {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin-bottom: 7px;

      .last-update {
        text-align: center;
      }
    }

    .past-week-icon {
      width: 14px;
    }

    .window-size-selection {
      margin-top: 25px;
      margin-bottom: 0px;
      display: flex;
      justify-content: center;
      border-bottom: 1px solid #efebeb;
      border-top: 1px solid #efebeb;
      padding-top: 2px;
      padding-bottom: 2px;
    }

    .show-timeseries {
      margin-top: 25px;
    }

    .past-evolution {
      margin-top: 15px;
    }

    .no-data {
      padding: 30px;
      padding-bottom: 5px;
      display: flex;
      .info-text {
        margin-left: 15px;
        user-select: none;
      }
    }
  }
</style>

<style lang="scss">
  #last-remote-measurement {
    .data-graph .plotly, .data-graph {
      margin-top: 5px;
      height: 220px;
      width: 100%;
    }
  }
</style>
